import React from 'react';
import { CheckIcon, PlusIcon, UsersIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/Card';
interface Opportunity {
  dayName: string;
  dayNum: string;
  month: string;
  title: string;
  badge: string;
  badgeType: 'soon' | 'later';
  checkoutTime: string;
  checkinTime: string;
  filledSpots: number;
  totalSpots: number;
  savings: number;
  guestName: string;
  bookingPlatform: string;
  bookingUrl: string;
  hostsInZone: number;
}
const opportunities: Opportunity[] = [{
  dayName: 'MAR',
  dayNum: '23',
  month: 'Gen',
  title: 'Pulizia turnover',
  badge: 'Domani',
  badgeType: 'soon',
  checkoutTime: '10:00',
  checkinTime: '15:00',
  filledSpots: 4,
  totalSpots: 5,
  savings: 18,
  guestName: 'Marco R.',
  bookingPlatform: 'Booking.com',
  bookingUrl: 'https://www.booking.com',
  hostsInZone: 12
}, {
  dayName: 'MER',
  dayNum: '24',
  month: 'Gen',
  title: 'Pulizia turnover',
  badge: 'Dopodomani',
  badgeType: 'soon',
  checkoutTime: '11:00',
  checkinTime: '16:00',
  filledSpots: 2,
  totalSpots: 5,
  savings: 12,
  guestName: 'Sophie L.',
  bookingPlatform: 'Airbnb',
  bookingUrl: 'https://www.airbnb.com',
  hostsInZone: 8
}, {
  dayName: 'SAB',
  dayNum: '27',
  month: 'Gen',
  title: 'Deep cleaning',
  badge: 'Tra 5 giorni',
  badgeType: 'later',
  checkoutTime: '10:00',
  checkinTime: '16:00',
  filledSpots: 1,
  totalSpots: 4,
  savings: 8,
  guestName: 'Giovanni P.',
  bookingPlatform: 'Expedia',
  bookingUrl: 'https://www.expedia.com',
  hostsInZone: 5
}, {
  dayName: 'LUN',
  dayNum: '29',
  month: 'Gen',
  title: 'Pulizia turnover',
  badge: 'Tra 7 giorni',
  badgeType: 'later',
  checkoutTime: '10:00',
  checkinTime: '15:00',
  filledSpots: 3,
  totalSpots: 5,
  savings: 15,
  guestName: 'Anna K.',
  bookingPlatform: 'Booking.com',
  bookingUrl: 'https://www.booking.com',
  hostsInZone: 10
}, {
  dayName: 'MER',
  dayNum: '31',
  month: 'Gen',
  title: 'Pulizia turnover',
  badge: 'Tra 9 giorni',
  badgeType: 'later',
  checkoutTime: '11:00',
  checkinTime: '16:00',
  filledSpots: 1,
  totalSpots: 4,
  savings: 10,
  guestName: 'Thomas W.',
  bookingPlatform: 'Airbnb',
  bookingUrl: 'https://www.airbnb.com',
  hostsInZone: 6
}, {
  dayName: 'SAB',
  dayNum: '3',
  month: 'Feb',
  title: 'Deep cleaning',
  badge: 'Tra 12 giorni',
  badgeType: 'later',
  checkoutTime: '10:00',
  checkinTime: '16:00',
  filledSpots: 2,
  totalSpots: 5,
  savings: 14,
  guestName: 'Elena M.',
  bookingPlatform: 'Expedia',
  bookingUrl: 'https://www.expedia.com',
  hostsInZone: 9
}];
function CapacityDots({
  filled,
  total,
  hostsInZone
}: {
  filled: number;
  total: number;
  hostsInZone: number;
}) {
  const remaining = total - filled;
  const isUrgent = remaining <= 1;
  return <div className="flex items-center gap-2.5 flex-wrap" data-id="element-1001">
      <div className="flex items-center gap-1.5" data-id="element-1002">
        {Array.from({
        length: filled
      }).map((_, i) => <div key={`filled-${i}`} className="w-6 h-6 rounded-full flex items-center justify-center bg-muted" data-id="element-1003">
            <CheckIcon size={13} className="text-muted-foreground" strokeWidth={2.5} data-id="element-1004" />
          </div>)}
        {Array.from({
        length: remaining
      }).map((_, i) => <div key={`empty-${i}`} className="w-6 h-6 rounded-full bg-white border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-foreground/40 hover:bg-muted transition-colors" data-id="element-1005">
            <PlusIcon size={13} className="text-muted-foreground" strokeWidth={2} data-id="element-1006" />
          </div>)}
      </div>
      <span className={`text-xs ${isUrgent ? 'font-medium text-coral' : 'text-muted-foreground'}`} data-id="element-1007">
        {remaining} {remaining === 1 ? 'posto rimasto' : 'posti rimasti'}
      </span>
    </div>;
}
function OpportunityCard({
  item
}: {
  item: Opportunity;
}) {
  return <Card data-id="element-1008">
      <CardContent noPadding className="p-4 sm:p-5" data-id="element-1009">
        {/* Mobile: stacked layout, sm+: row layout */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-5" data-id="element-1010">
          {/* Top row: Date block + Content */}
          <div className="flex gap-4 sm:gap-5 flex-1 min-w-0" data-id="element-1011">
            {/* Date block */}
            <div className="flex-shrink-0 w-14 text-center border border-border rounded-lg py-2 px-1.5" data-id="element-1012">
              <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground" data-id="element-1013">
                {item.dayName}
              </div>
              <div className="text-2xl font-bold text-foreground leading-tight" data-id="element-1014">
                {item.dayNum}
              </div>
              <div className="text-[11px] text-muted-foreground" data-id="element-1015">
                {item.month}
              </div>
            </div>

            {/* Center content */}
            <div className="flex-1 min-w-0" data-id="element-1016">
              {/* Title + Badge */}
              <div className="flex items-center gap-2 flex-wrap mb-2" data-id="element-1017">
                <h3 className="text-sm font-semibold text-foreground" data-id="element-1018">
                  {item.title}
                </h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${item.badgeType === 'soon' ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`} data-id="element-1019">
                  {item.badge}
                </span>
              </div>

              {/* Guest name + booking link */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2.5 flex-wrap" data-id="element-1020">
                <span className="text-muted-foreground" data-id="element-1021">Guest:</span>
                <span className="font-medium text-foreground" data-id="element-1022">
                  {item.guestName}
                </span>
                <span data-id="element-1023">·</span>
                <a href={item.bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-coral hover:underline text-foreground" data-id="element-1024">
                  {item.bookingPlatform}
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1025">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" data-id="element-1026" />
                    <polyline points="15 3 21 3 21 9" data-id="element-1027" />
                    <line x1="10" y1="14" x2="21" y2="3" data-id="element-1028" />
                  </svg>
                </a>
              </div>

              {/* Capacity dots - visible on sm+ in center column */}
              <div className="hidden sm:block" data-id="element-1029">
                <CapacityDots filled={item.filledSpots} total={item.totalSpots} hostsInZone={item.hostsInZone} data-id="element-1030" />
              </div>
            </div>
          </div>

          {/* Right column on sm+, bottom section on mobile */}
          <div className="flex flex-col sm:flex-shrink-0 sm:items-end sm:justify-between gap-3 sm:gap-0" data-id="element-1031">
            {/* Mobile: horizontal row with savings + button */}
            <div className="flex items-center justify-between sm:flex-col sm:items-end gap-3 sm:gap-2" data-id="element-1032">
              <div className="text-left sm:text-right" data-id="element-1033">
                <div className="text-sm font-bold text-coral" data-id="element-1034">
                  -€{item.savings}
                </div>
                <div className="text-[10px] text-muted-foreground" data-id="element-1035">
                  risparmi
                </div>
              </div>
              <Button variant="outline" size="sm" data-id="element-1036">
                Unisciti
              </Button>
            </div>

            {/* Capacity dots - visible on mobile only */}
            <div className="sm:hidden" data-id="element-1037">
              <CapacityDots filled={item.filledSpots} total={item.totalSpots} hostsInZone={item.hostsInZone} data-id="element-1038" />
            </div>

            {/* Hosts in zone text */}
            <span className="inline-flex items-center gap-1 text-muted-foreground text-xs sm:mt-1.5 sm:whitespace-nowrap" data-id="element-1039">
              <UsersIcon size={11} className="flex-shrink-0" data-id="element-1040" />
              <span className="line-clamp-1" data-id="element-1041">
                {item.hostsInZone} host con check-out lo stesso giorno
              </span>
            </span>
          </div>
        </div>
      </CardContent>
    </Card>;
}
export function SharingOpportunitiesWidget({
  maxItems
}: {
  maxItems?: number;
} = {}) {
  const displayedItems = maxItems ? opportunities.slice(0, maxItems) : opportunities;
  return <div data-id="element-1042">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-3" data-id="element-1043">
        <h2 className="text-sm font-semibold text-foreground" data-id="element-1044">
          Opportunità sharing
        </h2>
        <span className="text-xs text-muted-foreground" data-id="element-1045">
          {opportunities.length} disponibili
        </span>
      </div>

      {/* Opportunity list */}
      <div className="space-y-3" data-id="element-1046">
        {displayedItems.map((item, idx) => <OpportunityCard key={idx} item={item} data-id="element-1047" />)}
      </div>
    </div>;
}